/** Chemins API — hub embarqué (legacy) ou PWA standalone (NDD dédié). */

const DEFAULT_SUITE_HUB_ORIGIN = "https://auth.app.aureliasuite.fr";
const DEFAULT_SUITE_HUB_BASE = "/suite";

function readEnv(key: string): string | undefined {
  const raw = import.meta.env[key];
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed.replace(/\/+$/u, "") : undefined;
}

export function isMailStandalone(): boolean {
  return import.meta.env.VITE_MAIL_STANDALONE === "true";
}

/** Origin auth.app (SSO, api_suite, liens Coffre/Photos). */
export function getSuiteHubOrigin(): string {
  return readEnv("VITE_SUITE_HUB_ORIGIN") ?? DEFAULT_SUITE_HUB_ORIGIN;
}

/**
 * Préfixe URL sur auth.app.
 * Standalone : DocumentRoot = dossier `suite/` → pas de préfixe `/suite` (api = `/api_suite`).
 * Legacy hub iframe : préfixe `/suite` si l’app est sous auth.app/suite/…
 */
export function getSuiteHubBasePath(): string {
  const defaultBase = isMailStandalone() ? "" : DEFAULT_SUITE_HUB_BASE;
  const base = readEnv("VITE_SUITE_HUB_BASE") ?? defaultBase;
  if (!base || base === "/") return "";
  return base.startsWith("/") ? base : `/${base}`;
}

function joinUrl(origin: string, basePath: string, suffix: string): string {
  const parts = [origin.replace(/\/+$/u, ""), basePath.replace(/^\/+|\/+$/gu, ""), suffix.replace(/^\/+/u, "")].filter(
    Boolean
  );
  return parts.join("/");
}

/**
 * Préfixe same-origin de l’app courante (legacy hub iframe).
 * Standalone PWA : chaîne vide (DocumentRoot = suite/mail/dist).
 */
export function getSuiteBasePath(): string {
  if (isMailStandalone()) {
    return "";
  }

  let p = window.location.pathname || "/";
  if (p.endsWith("/index.html")) p = p.slice(0, -"/index.html".length);
  else if (/\.html$/i.test(p)) p = p.replace(/\/[^/]+$/u, "");
  p = p.replace(/\/+$/u, "");

  if (p.endsWith("/suite/mail/dist") || p.endsWith("/suite/mail")) {
    return p.replace(/\/suite\/mail(?:\/dist)?$/u, "/suite");
  }
  if (p.endsWith("/mail/dist") || p.endsWith("/mail")) {
    p = p.replace(/\/mail(?:\/dist)?$/u, "");
  }
  return p === "" || p === "/" ? "" : p;
}

/** API Suite (Coffre, Photos, SSO) — same-origin en PWA (`/api_suite` alias vhost). */
export function getSuiteApiBase(): string {
  if (isMailStandalone()) {
    return "/api_suite";
  }
  const base = getSuiteBasePath();
  return base ? `${base}/api_suite` : "/api_suite";
}

/**
 * API Coffre — même origin `/api_suite` (alias vhost, cookie session `.aureliasuite.fr`).
 * Évite les 401 cross-origin mail.app → auth.app quand le cookie hub n’est pas envoyé.
 */
export function getCoffreSuiteApiBase(): string {
  return getSuiteApiBase();
}

/** API Mail IMAP/SMTP — toujours same-origin (alias vhost `/api_mail`). */
export function getMailApiBase(): string {
  if (isMailStandalone()) {
    return "/api_mail";
  }
  const base = getSuiteBasePath();
  return base ? `${base}/api_mail` : "/api_mail";
}

export function getMailPingUrl(): string {
  return `${getMailApiBase()}/mail-ping.php`;
}

export function getSuiteWhoamiUrl(): string {
  return `${getSuiteApiBase()}/session/whoami.php`;
}

export function getHub2WhoamiUrl(): string {
  return `${getSuiteApiBase()}/hub2-whoami.php`;
}

export function getSsoVerifyUrl(userId: string | number, appName: string): string {
  return `${getSuiteApiBase()}/sso-verify.php?suite_user_id=${encodeURIComponent(String(userId))}&app=${encodeURIComponent(appName)}`;
}

/** Lien vers une autre app du hub (Coffre, Photos, …). */
export function getSuiteAppUrl(relativePath: string): string {
  const path = relativePath.replace(/^\//, "");
  if (isMailStandalone()) {
    return joinUrl(getSuiteHubOrigin(), getSuiteHubBasePath(), path);
  }
  const base = getSuiteBasePath();
  return `${window.location.origin}${base ? `${base}/` : "/"}${path}`;
}
